import React, { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { HubConnectionState } from '@microsoft/signalr'
import { TokenStorage } from 'api/tokenStorage'
import { Link } from 'components/Link'
import { ChevronDown } from 'lucide-react'
import { persistor } from 'store/store'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { WebSocketContext } from 'utils/WebSocketContext'

import { Popover, PopoverContent, PopoverHeader, PopoverTrigger } from '../ui/popover'

import './Avatar.scss'

import { useApiClient } from '../../utils/api/ApiClient'
import { IdentityApiClient } from '../../utils/api/identityApi'
import { Body2, Caption1, Title2 } from '../ui/typography'
import UserAvatar from './UserAvatar'

interface ProfileProps {
  triggerMenuOnClick?: boolean
  style?: {}
  bigText?: boolean

  status?: HubConnectionState
}

const UserInfo = ({
  username,
  email,
  status,
}: {
  username: string
  email: string
  avatar: React.ReactNode
  status?: HubConnectionState
}) => (
  <div className='popover'>
    <div className='popover__userInfo'>
      <Title2>{username}</Title2>
      <Caption1>{email}</Caption1>

      {status && (
        <Body2>
          Status: <b>{status}</b>
        </Body2>
      )}
    </div>
  </div>
)

const ProfileContent = (props: { handleLogout: () => void }) => {
  return (
    <div className='popover__menu'>
      <Link to={{ pathname: '/profile/edit' }} data-test='my-account-profile-link'>
        <Body2>Profile</Body2>
      </Link>
      <Link to='#' onClick={props.handleLogout} data-test='my-account-logout-link'>
        <Body2>Logout</Body2>
      </Link>
    </div>
  )
}

const Profile: React.FC<ProfileProps> = (props) => {
  const [auth, setAuth] = useContext(AuthSessionContext)
  const { hub } = useContext(WebSocketContext)
  const identityApiClient = useApiClient(IdentityApiClient)

  const navigate = useNavigate()

  const handleLogout = async () => {
    const sessionId = TokenStorage.getSession()
    await hub?.client.stop()
    await identityApiClient.signOut(sessionId)
    setAuth(null)

    persistor.purge()
    navigate(links.login.url)
  }

  if (!auth) return null

  const username = `${auth.user.firstName} ${auth.user.lastName}`
  const email = auth.user.email || ''
  const avatar = <UserAvatar bigText={props.bigText} fullName={username} style={props.style} />

  if (props.triggerMenuOnClick) {
    return (
      <Popover>
        <PopoverTrigger>
          <button
            type='button'
            data-test='my-account-button'
            className='avatar-text-block avatar-account-button'
          >
            <Title2 className='avatar-text-username'>{username}</Title2>

            <Body2 className='avatar-text-arrow'>
              <ChevronDown className='size-4' />
            </Body2>
          </button>
        </PopoverTrigger>

        <PopoverContent align='end' side='bottom' sideOffset={8} className='p-0'>
          <PopoverHeader className='p-2.5'>
            <UserInfo username={username} email={email} avatar={avatar} status={props.status} />
          </PopoverHeader>

          <div className='p-2.5 pt-0'>
            <ProfileContent handleLogout={handleLogout} />
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return <div className='avatar-container'>{avatar}</div>
}

export default Profile
